import React from 'react';
import { motion } from 'framer-motion';
import { 
  Star, 
  Quote, 
  Building2, 
  TrendingUp,
  Shield,
  Users
} from 'lucide-react';

const Testimonials: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const testimonials = [
    {
      name: "Marie Dubois",
      role: "Directrice de réseau scolaire",
      company: "Écoles du Savoir",
      content: "EXELIUS a révolutionné la gestion de nos 12 écoles. La transparence financière est maintenant totale et le gain de temps considérable.",
      rating: 5,
      avatar: "MD",
      stats: "Réduction de 40% du temps administratif"
    },
    {
      name: "Thomas Martin",
      role: "Propriétaire de restaurants",
      company: "Groupe Gourmet",
      content: "Gérer 8 restaurants était un cauchemar administratif. EXELIUS a tout simplifié. Les rapports automatiques me font gagner 20h par semaine.",
      rating: 5,
      avatar: "TM",
      stats: "8 établissements gérés simultanément"
    },
    {
      name: "Sophie Laurent",
      role: "Superviseur de cliniques",
      company: "Santé Plus Clinics",
      content: "La sécurité des données médicales était notre priorité. EXELIUS offre le niveau de sécurité dont nous avions besoin tout en restant simple d'utilisation.",
      rating: 5,
      avatar: "SL",
      stats: "Conformité RGPD garantie"
    }
  ];

  const stats = [
    {
      icon: Building2,
      value: "500+",
      label: "Établissements actifs",
      description: "Dans 15 pays différents"
    },
    {
      icon: Users,
      value: "1,200+",
      label: "Utilisateurs satisfaits",
      description: "Superviseurs et secrétaires"
    },
    {
      icon: TrendingUp,
      value: "98%",
      label: "Taux de satisfaction",
      description: "Clients qui recommandent"
    },
    {
      icon: Shield,
      value: "100%",
      label: "Sécurité garantie",
      description: "Aucune faille de sécurité"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <motion.div 
          {...fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ils nous font confiance
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez comment EXELIUS transforme la gestion d'établissements pour des centaines d'organisations
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div 
          {...fadeInUp}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white p-6 rounded-xl shadow-lg text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 text-blue-600 mb-4">
                <stat.icon className="h-6 w-6" />
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {stat.value}
              </div>
              <div className="font-semibold text-gray-900 mb-1">
                {stat.label}
              </div>
              <div className="text-sm text-gray-600">
                {stat.description}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Testimonials */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{ y: -5 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 h-full flex flex-col">
                {/* Quote icon */}
                <div className="mb-6">
                  <Quote className="h-10 w-10 text-blue-100 group-hover:text-blue-200 transition-colors" />
                </div>
                
                {/* Rating */}
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                {/* Content */}
                <p className="text-gray-700 mb-6 flex-grow italic">
                  "{testimonial.content}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.role}
                    </div>
                    <div className="text-sm text-blue-600 font-medium">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
                
                {/* Stat */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="text-sm font-medium text-gray-900">
                    {testimonial.stats}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Case Studies */}
        <motion.div 
          {...fadeInUp}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 md:p-12 text-white"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                Étude de cas : Réseau de 12 écoles
              </h3>
              <p className="text-blue-100 mb-6">
                Découvrez comment EXELIUS a aidé un réseau scolaire à réduire ses 
                coûts administratifs de 60% tout en améliorant la transparence financière.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-bold">60%</div>
                  <div className="text-sm text-blue-200">Réduction des coûts admin</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">12</div>
                  <div className="text-sm text-blue-200">Écoles gérées</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">40h</div>
                  <div className="text-sm text-blue-200">Gain de temps/mois</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">100%</div>
                  <div className="text-sm text-blue-200">Satisfaction des parents</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-xl p-6 backdrop-blur-sm">
              <h4 className="font-semibold mb-4">Témoignage du directeur :</h4>
              <blockquote className="italic text-blue-100 mb-4">
                "Avant EXELIUS, nous passions plus de temps sur l'administration que 
                sur l'éducation. Aujourd'hui, chaque école est autonome financièrement 
                tout en restant parfaitement contrôlée."
              </blockquote>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold">Jean Dupont</div>
                  <div className="text-sm text-blue-200">Directeur Général</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Trust badges */}
        <motion.div 
          {...fadeInUp}
          className="mt-16 text-center"
        >
          <h4 className="text-gray-600 mb-8">
            Reconnu et certifié par les leaders du secteur
          </h4>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-70">
            <div className="text-gray-400 font-bold text-xl">ISO 27001</div>
            <div className="text-gray-400 font-bold text-xl">GDPR Compliant</div>
            <div className="text-gray-400 font-bold text-xl">SOC 2 Type II</div>
            <div className="text-gray-400 font-bold text-xl">PCI DSS</div>
            <div className="text-gray-400 font-bold text-xl">HIPAA</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;